import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

const OctopusPage = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    'https://images.charterindex.com/b90d7c3d-9321-42ba-b378-dbdc3f44e331.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/8089a7fb-0a40-4792-aee7-d0d2769442f8.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/12593f4e-b79c-4497-b427-dc67f9eff1db.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/0c164ba6-0b6c-46c8-8060-f692fde5d83a.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/7bc14a79-526c-417c-b64a-c73749821725.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/714990a9-3460-4c4f-927d-6c61f84ed4a8.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/7fb33e66-f42f-4d65-9960-a99eb6a98c7e.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/0698163d-5ca0-437f-88f6-57a31f27f5f0.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/4ba47f34-113a-4194-b30d-6b5226a15509.jpg?auto=format,compress&q=50&lossless=true',
    'https://images.charterindex.com/1ea9d2f9-6e31-446c-93d2-5d21d0314dc7.jpg?auto=format,compress&q=50&lossless=true'
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section with Image Gallery */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/yacht-charter')}
            className="mb-8 flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors font-medium"
          >
            <i className="ri-arrow-left-line"></i>
            BACK TO CHARTER FLEET
          </button>

          {/* Image Gallery */}
          <div className="relative mb-12 rounded-lg overflow-hidden bg-gray-100" style={{ height: '600px' }}>
            <img
              src={images[currentImageIndex]}
              alt={`OCTOPUS - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all shadow-lg"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all shadow-lg"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>

          {/* Yacht Details */}
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h1 className="text-5xl font-bold mb-4">OCTOPUS</h1>
              <p className="text-2xl text-gray-600 mb-8">414ft Lürssen Expedition Yacht</p>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  The legendary 126.2-meter (414ft) expedition yacht OCTOPUS represents the pinnacle of exploration and luxury. Built by the renowned German shipyard Lürssen in 2003, this magnificent vessel combines ice-class expedition capabilities with world-class amenities and accommodations.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  OCTOPUS features an ice-class hull enabling safe navigation in polar regions, dual helipads for convenient access, and an impressive array of expedition equipment including two submarines, a remotely operated vehicle (ROV), and extensive water sports equipment. The yacht's glass-bottomed aquarium observation lounge offers a unique underwater viewing experience.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  The interior accommodates up to 12 guests in 11 luxurious cabins, attended by a professional crew of 42. Amenities include a spectacular beach club, swimming pool, fully equipped gym and spa, dive center, cinema, and multiple entertainment areas. The observation lounge provides panoramic views for wildlife watching and stargazing.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  With her exceptional range and expedition capabilities, OCTOPUS can access the world's most remote and spectacular destinations, from the Arctic to Antarctica, while providing guests with uncompromising luxury and comfort. Her extensive tender garage houses multiple expedition tenders and water toys for exploration and adventure.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  Whether exploring pristine polar regions, diving remote coral reefs, or cruising the Mediterranean in style, OCTOPUS offers an unparalleled charter experience combining adventure, luxury, and the freedom to discover the world's most extraordinary destinations.
                </p>
              </div>

              {/* Key Features */}
              <div className="mt-12">
                <h2 className="text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Ice-Class Hull for Polar Exploration',
                    'Dual Helipads',
                    'Two Submarines',
                    'Glass-Bottomed Aquarium Lounge',
                    'Beach Club & Swimming Pool',
                    'Fully Equipped Gym & Spa',
                    'Professional Dive Center',
                    'Cinema & Entertainment Areas',
                    'Observation Lounge',
                    'Extensive Water Toys & Tenders',
                    'ROV (Remotely Operated Vehicle)',
                    'Extended Range Capabilities'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Specifications Card */}
              <div className="bg-gray-50 rounded-lg p-8 mb-8 sticky top-24">
                <h3 className="text-xl font-bold mb-6">Specifications</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Length</span>
                    <span className="font-semibold">126.2m / 414ft</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Builder</span>
                    <span className="font-semibold">Lürssen</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Year</span>
                    <span className="font-semibold">2003</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Guests</span>
                    <span className="font-semibold">12</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Cabins</span>
                    <span className="font-semibold">11</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Crew</span>
                    <span className="font-semibold">42</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Type</span>
                    <span className="font-semibold">Expedition Yacht</span>
                  </div>
                  <div className="pt-4">
                    <div className="text-gray-600 mb-2">Weekly Charter Rate</div>
                    <div className="text-3xl font-bold text-teal-600">$2,200,000</div>
                    <div className="text-sm text-gray-500 mt-1">Plus expenses</div>
                  </div>
                </div>

                <button className="w-full mt-8 bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  REQUEST CHARTER INFORMATION
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OctopusPage;
